import UserInviteButton from "./user-invite-button";
import {
    BooleanIcon,
    Cell,
    Column,
    Header,
    Heading,
    None,
    Pagination,
    Table,
    TableColumns,
    TableFooter,
} from "@edgetier/client-components";
import { formatDistance } from "date-fns";
import { FunctionComponent, memo } from "react";
import { IProps } from "./users-table.types";
import { IUser } from "@edgetier/watchtower-types";
import useRoles from "hooks-for/permissions/use-roles";
import UserActionsMenu from "./user-actions-menu";
import "./users-table.scss";

/**
 * A table displaying all of the existing Watchtower users.
 * @param props.users      The users to be displayed in the table.
 * @param props.tableProps Some optional table props.
 */
const UsersTable: FunctionComponent<IProps> = ({ users, ...tableProps }) => {
    const { data: roles } = useRoles();
    return (
        <div className="users-table">
            <Table getRowKey={({ userId }) => userId} rows={users} pageSize={10} {...tableProps}>
                <Header>
                    <Pagination showPageNumbers={false} />
                </Header>
                <TableColumns>
                    <Column>
                        <Heading>Name</Heading>
                        <Cell<IUser>>{({ firstName, surname }) => `${firstName} ${surname}`}</Cell>
                    </Column>

                    <Column>
                        <Heading sortProperty="emailAddress">Email Address</Heading>
                        <Cell<IUser>>{({ emailAddress }) => emailAddress}</Cell>
                    </Column>

                    <Column>
                        <Heading>Role</Heading>
                        <Cell<IUser>>
                            {(user) => roles?.find(({ roleId }) => roleId === user.roleId)?.role ?? user.role}
                        </Cell>
                    </Column>

                    <Column>
                        <Heading className="users-table__alerts-enabled">Anomaly Alerts Enabled</Heading>
                        <Cell<IUser>>{({ alertsEnabled }) => <BooleanIcon value={alertsEnabled} />}</Cell>
                    </Column>

                    <Column>
                        <Heading>Last Sign In</Heading>
                        <Cell<IUser>>
                            {({ lastAuthenticationDateTime }) =>
                                lastAuthenticationDateTime instanceof Date ? (
                                    `${formatDistance(lastAuthenticationDateTime, new Date())} ago`
                                ) : (
                                    <None>N/A</None>
                                )
                            }
                        </Cell>
                    </Column>

                    <Column>
                        <Heading>&nbsp;</Heading>
                        <Cell<IUser>>{(user) => <UserActionsMenu user={user} />}</Cell>
                    </Column>
                </TableColumns>
                <TableFooter>
                    <UserInviteButton />
                </TableFooter>
            </Table>
        </div>
    );
};

export default memo(UsersTable);
